import React from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import Modal from 'react-modal';
import 'react-day-picker/lib/style.css';
import { modalStyle } from '../../constants';
import { milestoneToTitle, NBSP } from '../../utils/';
import AdjustForm from './schedule_modal_form';
import { getDateLimits, getDateLimitsActual, DateField, DateFieldActual } from './schedule_utils';
import { getMILESTONES, NAMES, isClientAdjustable } from './schedule_constants';
import { getDateValue, backToDate } from '../../utils/date';

delete Modal.defaultStyles.content.padding;

const noop = () => {};
const clone = (obj) => JSON.parse(JSON.stringify(obj));

const isStartMilestone = (milestone) => 
  (milestone === 'designStart' || milestone === 'constructionStart')

const isCompletionMilestone = (milestone) =>
  (milestone === 'designCompletion' || milestone ===  'constructionCompletion')

const clearAlertText = 'This clears the entire schedule. This is for testing purpose only. Are you sure?'

// STAGES
// Using integers with gaps so that we can fit more cases in future
const ORIGINALS_INCOMPLETE            = 10
const ORIGINALS_COMPLETE              = 20
const ORIGINALS_LOCKED                = 30
const SHOW_COPY_ORIGINALS             = 35
const PROJECTED_LOCKED                = 40
const SHOW_SET_DESIGN_BASELINE        = 50
const DESIGN_BASELINE_SET             = 60
const SHOW_SET_CONSTRUCTION_BASELINE  = 70
const CONSTRUCTION_BASELINE_SET       = 80

class ScheduleForm extends React.Component {
  // static PropTypes = {
  //   divisionName: PropTypes.string.isRequired,
  //   onCancel: PropTypes.func.isRequired,
  //   save: PropTypes.func.isRequired,
  //   schedule: PropTypes.object.isRequired,
  //   scheduleStage: PropTypes.string,
  //   showMasterEdit: PropTypes.bool
  // }

  constructor (props) {
    super(props);
    const schedule = clone(props.schedule);

    this.state = {
      schedule,
      batch: {},
      modalBatch: {},
      allValid: true,
      isSaving: false,
      alertType: '',
      currentMilestone: '',
      modalMilestone: '',
      modalName: '',
      masterMode: false,
      settingNow: '',
    }

    this.MILESTONES = getMILESTONES(props.divisionName);
    this.toTitle = (milestone) => milestoneToTitle(milestone, props.divisionName);

    this.undos = [];
    this.newStage = null;

    this.handleExit = this.handleExit.bind(this);
    this.handleSave = this.handleSave.bind(this);
    this.triggerModal = this.triggerModal.bind(this);
    this.toggleMasterMode = this.toggleMasterMode.bind(this);
    this.dismissAlert = this.dismissAlert.bind(this);

    this.redoDesignBaseline = this.redoDesignBaseline.bind(this);
    this.setupDesignBaseline = this.setupDesignBaseline.bind(this);
    this.redoConstructionBaseline = this.redoConstructionBaseline.bind(this);
    this.setupConstructionBaseline = this.setupConstructionBaseline.bind(this);
    this.askToVerifyProjected = this.askToVerifyProjected.bind(this);

    this.onUndo = this.onUndo.bind(this);
    this.changeHandler = this.changeHandler.bind(this);
    this.clearWhole = this.clearWhole.bind(this);
    this.lockOriginals = this.lockOriginals.bind(this);
    this.copyOriginals = this.copyOriginals.bind(this);

    this.setCEBFeatureAlert = this.setCEBFeatureAlert.bind(this);
    this.saveProjected = this.saveProjected.bind(this);
    this.saveDesignBaseline = this.saveDesignBaseline.bind(this);
    this.saveConstructionBaseline = this.saveConstructionBaseline.bind(this);
    this.submitAdjustForm = this.submitAdjustForm.bind(this);
  }

  getInitialSchedule() {
    return {...this.props.schedule};
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    this.undos = [];
    this.newStage = nextProps.scheduleStage;
    const { batch, schedule } = this.getNewBatchSchedule(nextProps.schedule);

    this.setState({
      batch,
      schedule,
      modalBatch: {},
      isSaving: false,
    });
  }

  getNewBatchSchedule (newSchedule) {
    const batch = {};
    const schedule = clone(newSchedule);
    const comment = 'Copy from Projected';

    const actualDesignStart = _.get(this.state.batch, 'designStart.actual.value');
    const actualConstructionStart = _.get(this.state.batch, 'constructionStart.actual.value');

    if (actualDesignStart && !schedule.designCompletion.clientExtendedBaseline) {
      const value = schedule.designCompletion.projected;
      _.set(schedule, 'designCompletion.clientExtendedBaseline', value);
      _.set(batch, 'designCompletion.clientExtendedBaseline', {value, comment});
    } else if (actualConstructionStart && !schedule.constructionCompletion.clientExtendedBaseline) {
      const value = schedule.constructionCompletion.projected;
      _.set(schedule, 'constructionCompletion.clientExtendedBaseline', value);
      _.set(batch, 'constructionCompletion.clientExtendedBaseline', {value, comment});
    }

    return { batch, schedule }
  }

  guessCurrentStage (serverStage) {
    const { schedule } = this.state;

    let stage = ORIGINALS_INCOMPLETE;
    if ( this.hasValuesBefore(schedule, 'constructionBaseline', 'constructionCompletion') ) {
      stage = CONSTRUCTION_BASELINE_SET;
    } else if ( this.hasValuesBefore(schedule, 'actual', 'constructionCompletion') ) {
      stage = SHOW_SET_CONSTRUCTION_BASELINE;
    } else if ( this.hasValuesBefore(schedule, 'designBaseline', 'designStart') ) {
      stage = DESIGN_BASELINE_SET;
    } else if ( this.hasValuesBefore(schedule, 'actual', 'schematicDesignCompletion') ) {
      stage = SHOW_SET_DESIGN_BASELINE;
    } else if ( _.every(schedule, 'projected') && this.state.settingNow !== 'projected') {
      stage = PROJECTED_LOCKED;
    } else if ( serverStage === ORIGINALS_LOCKED && !_.every(schedule, 'projected') ) {
      stage = SHOW_COPY_ORIGINALS;
    } else if ( _.every(schedule, 'original') && _.every(schedule, 'projected') ) {
      stage = ORIGINALS_LOCKED;
    } else if ( _.every(schedule, 'original') ) {
      stage = ORIGINALS_COMPLETE;
    }
    return stage;
  }

  getCurrentStage () {
    const serverStage = parseInt(this.props.scheduleStage || 0, 10);
    const guessedStage = this.guessCurrentStage(serverStage);
    return Math.max(guessedStage, serverStage);
  }

  firstEmpty (schedule, name) {
    return this.MILESTONES.map(m => schedule[m][name]).indexOf(null);
  }

  hasValuesBefore (schedule, name, milestone) {
    const mIndex = this.MILESTONES.indexOf(milestone);
    return _.every(this.MILESTONES.slice(0, mIndex).map(m => schedule[m][name]));
  }

  changeHandler (milestone, name, comment='Update value') {
    return (formatted) => {
      const value = backToDate(formatted);
      this.updateUndos();
      this.updateBatch(milestone, name, value, comment);
      this.setBatchState({currentMilestone: milestone});
      const projectedValue = this.getValue(milestone, 'projected');

      if (name === 'actual' && value < projectedValue) {
        this.setState({alertType: 'saveActual'});
      } else if (name === 'actual' && value) {
        if ( isStartMilestone(milestone) ) {
          this.setState({alertType: 'cebFeature'});
        } else if ( isCompletionMilestone(milestone) && value > projectedValue) {
          this.triggerModal(milestone, name);
        }
      }
    }
  }

  checkAllValid () {
    let allValid = true;
    _.each(this.state.batch, (nameObjects, milestone) => {
      _.each(nameObjects, (obj, name) => {
        const { isValid } = this.runValidations(milestone, name, obj.value);
        allValid = allValid && isValid;
      })
    })
    return allValid;
  }

  updateUndos (extraState) {
    if (!extraState) extraState = {};
    this.undos.push({
      batch: clone(this.state.batch),
      ...extraState
    });
  }

  updateBatch (milestone, name, value, comment) {
    const { batch } = this.state;
    _.set(batch, `${milestone}.${name}.value`, value);
    _.set(batch, `${milestone}.${name}.comment`, comment);
  }

  setBatchState (extraState) {
    const { batch, schedule } = this.state;

    _.each(batch, (nameObjects, milestone) => {
      _.each(nameObjects, (obj, name) => {
        _.set(schedule, `${milestone}.${name}`, obj.value);
      })
    })

    const allValid = this.checkAllValid();
    this.setState({schedule, batch, allValid, ...extraState});
  }

  saveBatchtoServer (callback) {
    const objects = [];
    _.each(this.state.batch, (nameObjects, milestone) => {
      _.each(nameObjects, (obj, name) => {
        objects.push(this.getPatchObject(milestone, name, obj.value, obj.comment));
      })
    });

    if (this.newStage) {
      objects.push({
        milestoneType: 'scheduleStage',
        milestoneValueType: 'scheduleStage',
        comment: 'Set stage',
        stage: this.newStage
      });
      this.newStage = null;
    }

    if (objects.length) this.saveObjects(objects, callback);
  }

  // This is a temporary function for testing
  getPatchObject (milestoneType, milestoneValueType, value, comment) {
    return { milestoneType, milestoneValueType, value, comment }
  }

  onUndo () {
    if (this.undos.length === 0) return;
    const { batch, ...extraState } = this.undos.pop()
    _.set(this.state, 'batch', batch);
    _.set(this.state, 'schedule', clone(this.props.schedule));
    this.setBatchState(extraState);
  }

  toggleMasterMode () {
    this.setState({masterMode: !this.state.masterMode});
  }

  saveObjects (objects, callback=noop) {
    this.setState({ isSaving: true });
    this.props.save(objects, callback);
  }

  clearWhole () {
    if (!window.confirm(clearAlertText)) return;

    // Client extended baseline has values only for two milestones
    const allPairs = [
      ['designCompletion', 'clientExtendedBaseline'],
      ['constructionCompletion', 'clientExtendedBaseline'],
    ];
    this.MILESTONES.forEach(milestone => {
      NAMES.slice(0, 5).forEach(name => {
        allPairs.push([milestone, name])
      });
    });

    allPairs.forEach(([milestone, name]) => {
      if (!this.getValue(milestone, name)) return;
      this.updateBatch(milestone, name, null, 'Clear');
    });

    this.newStage = '10';
    this.setBatchState({settingNow: ''})
    this.saveBatchtoServer();
  }

  copyOriginals () {
    const comment = 'Copying Originals';
    this.MILESTONES.forEach(milestone => {
      const value = this.getValue(milestone, 'original')
      this.updateBatch(milestone, 'projected' , value, comment);
    });
    this.setBatchState({settingNow: 'projected'});
  }

  lockOriginals () {
    const comment = 'Locking Originals';
    this.newStage = ORIGINALS_LOCKED.toString();
    this.saveBatchtoServer(() => {
      this.MILESTONES.forEach(milestone => {
        const value = this.getValue(milestone, 'original')
        this.updateBatch(milestone, 'projected' , value, comment);
      });
      this.setBatchState({settingNow: 'projected'});
    });
  }

  redoDesignBaseline () {
    if (!_.isEmpty(this.state.batch)) {
      this.setState({ alertType: 'askToSave' });
      return;
    }
    this.updateUndos({ settingNow: ''});
    this.setBatchState({settingNow: 'designBaseline'});
  }

  setupDesignBaseline () {
    if (!_.isEmpty(this.state.batch)) {
      this.setState({ alertType: 'askToSave' });
      return;
    }

    const comment = 'Setup Design Baselines';
    this.updateUndos({ settingNow: ''});
    this.MILESTONES.forEach(milestone => {
      const value = this.getValue(milestone, 'actual') || this.getValue(milestone, 'projected');
      this.updateBatch(milestone, 'designBaseline' , value, comment);
    });
    this.setBatchState({settingNow: 'designBaseline'});
  }

  redoConstructionBaseline () {
    if (!_.isEmpty(this.state.batch)) {
      this.setState({ alertType: 'askToSave' });
      return;
    }

    this.updateUndos({ settingNow: ''});
    this.setBatchState({settingNow: 'constructionBaseline'});
  }

  setupConstructionBaseline () {
    if (!_.isEmpty(this.state.batch)) {
      this.setState({ alertType: 'askToSave' });
      return;
    }

    const comment = 'Setup Construction Baselines';
    this.updateUndos({ settingNow: ''});
    this.MILESTONES.forEach(milestone => {
      const value = this.getValue(milestone, 'actual') || this.getValue(milestone, 'projected');
      this.updateBatch(milestone, 'constructionBaseline', value, comment);
    });
    this.setBatchState({settingNow: 'constructionBaseline'});
  }

  saveProjected () {
    this.saveBatchtoServer(() => {
      this.setState({ settingNow: '', alertType: '' });
    });
  }

  saveDesignBaseline () {
    this.saveBatchtoServer(() => {
      this.setState({ settingNow: '' });
    });
  }

  saveConstructionBaseline () {
    this.saveBatchtoServer(() => {
      this.setState({ settingNow: '' });
    });
  }

  submitAdjustForm (milestone, name) {
    return (newDate, reason, clientDays, clientDate) => {
      const projectedValue = backToDate(newDate);
      this.updateUndos();
      this.updateBatch(milestone, name, projectedValue, reason);
      if (clientDate) {
        const cebValue = backToDate(clientDate);
        this.updateBatch(milestone, 'clientExtendedBaseline', cebValue, reason);
      }

      const { modalBatch } = this.state;
      modalBatch[milestone] = {newDate, reason, clientDays};

      this.setBatchState({
        modalBatch,
        modalMilestone: '',
        modalName: '',
      });
    }
  }

  setCEBFeatureAlert () {
    if (this.state.masterMode) this.dismissAlert();
    this.setState({ alertType: 'cebFeature' });
  }

  triggerModal (modalMilestone, modalName) {
    this.setState({ modalMilestone, modalName })
  }

  dismissAlert () {
    this.setState({alertType: ''});
  }

  askToVerifyProjected () {
    this.setState({alertType: 'saveProjected'});
  }

  handleExit () {
    if (_.isEmpty(this.state.batch)) {
      this.props.onCancel();
    } else {
      this.setState({ alertType: 'confirmExit' });
    }
  }

  handleSave () {
    this.saveBatchtoServer(() => {
      this.setState({masterMode: false});
    });
  }

  getValue (milestone, name) {
    return this.state.schedule[milestone][name];
  }

  getErrorText (milestone, name, value, limits) {
    if (milestone === 'actual') {
      const originalValue = this.props.schedule['original'][name];
      if (value < originalValue) return 'should be later than Original';
    } else if (limits.dateAbove && value < limits.dateAbove) {
      return 'should be later than ' + this.toTitle(limits.nameAbove);
    } else if (limits.dateBelow && value > limits.dateBelow) {
      return 'should be before ' + this.toTitle(limits.nameBelow);
    }
    return '';
  }

  checkValidity (...args) {
    return this.getErrorText(...args) === '';
  }

  getDateLimits (schedule, milestone, name) {
    return getDateLimits(this.MILESTONES, schedule, milestone, name);
  }

  getDateLimitsActual (schedule, milestone, name) {
    return getDateLimitsActual(this.MILESTONES, schedule, milestone, name);
  }

  runValidations (milestone, name, value) {
    const { schedule } = this.state;
    const limits = this.getDateLimits(schedule, milestone, name);
    const isValid = this.checkValidity(milestone, name, value, limits);
    return { limits, isValid };
  }

  runValidationsActual (milestone, name, value) {
    const { schedule } = this.state;
    const limits = this.getDateLimitsActual(schedule, milestone, name);
    const isValid = this.checkValidity(milestone, name, value, limits);
    return { limits, isValid };
  }

  // renderSomeElement methods {{{
  // =============================

  renderDateField (milestone, name) {
    const value = this.getValue(milestone, name);
    const { limits, isValid } = this.runValidations(milestone, name, value);
    const errorText = !isValid ? this.getErrorText(milestone, name, value, limits) : '';
    const fieldProps = { value, limits, isValid, errorText };

    return (
      <DateField
        {...fieldProps}
        masterMode={this.state.masterMode}
        onChange={this.changeHandler(milestone, name)}
      />
    );
  }

  renderDateFieldActual (milestone, name) {
    const value = this.getValue(milestone, name);
    const { limits, isValid } = this.runValidationsActual(milestone, name, value);
    const errorText = !isValid ? this.getErrorText(milestone, name, value, limits) : '';
    const fieldProps = { value, limits, isValid, errorText };

    return (
      <DateFieldActual
        {...fieldProps}
        masterMode={this.state.masterMode}
        onChange={this.changeHandler(milestone, name)}
      />
    );
  }

  renderModalTrigger (milestone, name) {
    const value = this.getValue(milestone, name);
    const { limits, isValid } = this.runValidations(milestone, name, value);
    const errorText = !isValid ? this.getErrorText(milestone, name, value, limits) : '';

    return (
      <div
        className={'modal-trigger' + (errorText ? ' has-error' : '')}
        onClick={() => this.triggerModal(milestone, name)}>
        {getDateValue(value) || NBSP}
        { errorText ? <div className="tooltip">{errorText}</div> : null }
      </div>
    );
  }

  renderDate (milestone, name) {
    const value = this.getValue(milestone, name);
    const dateString = getDateValue(value) || NBSP;
    return <span className="input">{dateString}</span>
  }

  renderConfirmExitAlert () {
    return (
      <div>
        <h1>Alert!</h1>
        <p>You have some unsaved changes. Do you still want to exit?</p>
        <div className="actions">
          <button className="alert-save" onClick={this.props.onCancel}>Yes, Exit!</button>
          <button className="alert-cancel" onClick={this.dismissAlert}>No</button>
        </div>
      </div>
    );
  }

  renderSaveProjectedAlert () {
    return (
      <div>
        <h1>Alert!</h1>
        <p>Please verify entered projected dates. After saving the projected dates,
          it can be edited/managed as part of adjustment for which adjustment reason
          has to be specified</p>
        <div className="actions">
          <button className="alert-save" onClick={this.saveProjected}>Save Projected Dates</button>
          <button className="alert-cancel" onClick={this.dismissAlert}>Change</button>
        </div>
      </div>
    );
  }

  renderSaveActualAlert () {
    const { currentMilestone } = this.state;

    let saveMethod = this.dismissAlert;
    if (currentMilestone === 'designStart' || currentMilestone === 'constructionStart')
      saveMethod = this.setCEBFeatureAlert;

    return (
      <div>
        <h1>Alert!</h1>
        <p>Actual date is earlier than projected date.</p>
        <div className="actions">
          <button className="alert-save" onClick={saveMethod}>OK, Got it!</button>
        </div>
      </div>
    );
  }

  renderCEBFeatureAlert () {
    let milestoneTitle = 'Design Completion';
    if (this.state.currentMilestone === 'constructionStart')
      milestoneTitle = 'Construction Completion';

    return (
      <div>
        <h1>Alert!</h1>
        <p>On saving, <strong>Projected {milestoneTitle}</strong> date will be copied over
          to <strong>Client Ext. Baseline</strong>.
        </p>
        <div className="actions">
          <button className="alert-save" onClick={this.dismissAlert}>OK, Got it!</button>
        </div>
      </div>
    );
  }

  renderAskToSaveAlert () {
    return (
      <div>
        <h1>Alert!</h1>
        <p>Please save your changes before setting up Baseline values.</p>
        <div className="actions">
          <button className="alert-save" onClick={this.dismissAlert}>OK, Got it!</button>
        </div>
      </div>
    );
  }

  renderAlertModal () {
    const { alertType } = this.state;

    let ModalContent = null;
    if (alertType === 'saveProjected') {
      ModalContent = this.renderSaveProjectedAlert();
    } else if (alertType === 'saveActual') {
      ModalContent = this.renderSaveActualAlert();
    } else if (alertType === 'confirmExit') {
      ModalContent = this.renderConfirmExitAlert();
    } else if (alertType === 'cebFeature') {
      ModalContent = this.renderCEBFeatureAlert();
    } else if (alertType === 'askToSave') {
      ModalContent = this.renderAskToSaveAlert();
    }

    return (
      <Modal
        isOpen={!!alertType}
        style={modalStyle}
        contentLabel="Alert modal"
      >
        <div className="alert-modal">
          {ModalContent}
        </div>
      </Modal>
    );
  }

  renderModal () {
    const { modalMilestone, modalName } = this.state;
    const heading = this.toTitle(modalMilestone);

    let Form = null;

    if (modalMilestone) {
      let newValue = '';
      let value = this.props.schedule[modalMilestone][modalName] || '';

      if (modalName === 'actual') {
        value = this.getValue(modalMilestone, 'projected');
        newValue = this.getValue(modalMilestone, 'actual');
      }

      const hasClientDate = isClientAdjustable(modalMilestone);
      const existing = _.get(this.state.modalBatch, modalMilestone, {});

      // This forces the date field to open the correct month when there's
      // no existing value;
      const limits = this.getDateLimits(this.state.schedule, modalMilestone, modalName);
      limits.month = value;

      Form = (
        <AdjustForm
          value={value}
          newValue={newValue}
          existing={existing}
          limits={limits}
          hasClientDate={hasClientDate}
          onSubmit={this.submitAdjustForm(modalMilestone, modalName)}
        />
      );
    }

    return (
      <Modal
        isOpen={!!modalMilestone}
        style={modalStyle}
        contentLabel="Modal form"
      >
        <h1>{heading}</h1>
        {Form}
        <div className="modal-close"
          onClick={() => this.triggerModal('', '')}>
          <span className="close"></span>
        </div>
      </Modal>
    )
  }

  renderButton (method, title) {
    if (!this.state.allValid) return null;

    return (
      <button
        className="btn-table"
        onClick={method}
        disabled={this.state.isSaving}
      >
        {title}
      </button>
    )
  }

  // end renderSomeElement methods }}}

  // renderColumn methods {{{
  // ========================

  // Original column shows date inputs only when all other fields are empty.
  // When all Original values are entered, user can lock them by clicking
  // "Lock Originals". After this, this column never shows date inputs again.
  renderOriginal (milestone, stage) {
    const name = NAMES[0];  // = original

    return (stage > ORIGINALS_COMPLETE)
      ? this.renderDate(milestone, name)
      : this.renderDateField(milestone, name);
  }

  // Projected column is the most complex of the lot. It can have regular date
  // inputs, modal triggers of two different types. Please see comments near the
  // conditionals below, for a better idea.
  renderProjected (milestone, stage) {
    const name = NAMES[1];  // = projected
    const actualValue = this.props.schedule[milestone]['actual'];

    if (stage === SHOW_COPY_ORIGINALS) {
      if (milestone === _.last(this.MILESTONES)) {
        return this.renderButton(this.copyOriginals, 'Copy Originals');
      }
    // When all Original values are entered, show a button in the last row,
    // for saving those values.
    } else if (stage === ORIGINALS_COMPLETE && milestone === _.last(this.MILESTONES)) {
      return this.renderButton(this.lockOriginals, 'Lock Originals');

    // This state immediately follows "Lock Originals". User can freely enter data
    // in this column and press "Save Projected" button in the last row of next column
    } else if (this.state.settingNow === 'projected') {
      return this.renderDateField(milestone, name);

    // If there's actual value or some other edit is going on, just render date
    } else if (stage < ORIGINALS_LOCKED || actualValue || this.state.settingNow) {
      return this.renderDate(milestone, name);

    } else {
      return this.renderModalTrigger(milestone, name);
    }
  }

  renderActual (milestone, stage) {
    const name = NAMES[2];  // = actual
    const value = this.props.schedule[milestone]['actual'];

    if (this.state.settingNow === 'projected' && milestone === _.last(this.MILESTONES)) {
      return this.renderButton(this.askToVerifyProjected, 'Save Projected');
    } else if (value || this.state.settingNow) {
      return this.renderDate(milestone, name);
    } else if (stage === SHOW_SET_DESIGN_BASELINE && this.MILESTONES.indexOf(milestone) > 2) {
      return null;
    } else if (stage === SHOW_SET_CONSTRUCTION_BASELINE) {
      if (this.MILESTONES.indexOf(milestone) >= this.MILESTONES.indexOf('constructionCompletion'))
        return null;
    }

    const isEditable = this.hasValuesBefore(this.state.schedule, name, milestone) 
    if (isEditable && stage >= PROJECTED_LOCKED) {
      return this.renderDateFieldActual(milestone, name);
    }
  }

  renderDesignBaseline (milestone, stage) {
    const name = NAMES[3];  // = designBaseline
    const value = this.getValue(milestone, name);
    const firstEmptyActual = this.firstEmpty(this.state.schedule, 'actual');
    const canShowButton = (milestone === this.MILESTONES[firstEmptyActual]);

    if (this.state.settingNow === 'designBaseline' && this.MILESTONES.indexOf(milestone) >= firstEmptyActual) {
      return this.renderDateField(milestone, name);
    } else if (value) {
      return this.renderDate(milestone, name);
    } else if (stage === SHOW_SET_DESIGN_BASELINE && canShowButton) {
      return this.renderButton(this.setupDesignBaseline, 'Setup Design Baseline');
    }
  }

  renderConstructionBaseline (milestone, stage) {
    const name = NAMES[4];  // = constructionBaseline
    const value = this.getValue(milestone, name);
    const firstEmptyActual = this.firstEmpty(this.state.schedule, 'actual');
    const canShowButton = (milestone === this.MILESTONES[firstEmptyActual]);
    const { schedule, batch } = this.state;

    if (this.state.settingNow === 'designBaseline') {
      if (milestone === _.last(this.MILESTONES) && _.every(schedule, 'designBaseline') && !_.isEmpty(batch)) {
        return this.renderButton(this.saveDesignBaseline, 'Save baselines');
      }
    } else if (this.state.settingNow === 'constructionBaseline' && this.MILESTONES.indexOf(milestone) >= firstEmptyActual) {
      return this.renderDateField(milestone, name);
    } else if (stage === DESIGN_BASELINE_SET) {
      if (milestone === _.last(this.MILESTONES) && !this.hasValuesBefore(schedule, 'actual', 'constructionStart') ) {
        return this.renderButton(this.redoDesignBaseline, 'Re-Baseline');
      }
    } else if (value) {
      return this.renderDate(milestone, name);
    } else if (stage === SHOW_SET_CONSTRUCTION_BASELINE && canShowButton) {
      return this.renderButton(this.setupConstructionBaseline, 'Setup Construction Baseline');
    }
  }

  renderClientBaseline(milestone, stage) {
    const name = NAMES[5];  // = clientExtendedBaseline
    const { schedule, batch } = this.state;

    if (this.state.settingNow === 'constructionBaseline' && milestone === _.last(this.MILESTONES)) {
      if ( _.every(schedule, 'constructionBaseline') && !_.isEmpty(batch) ) {
        return this.renderButton(this.saveConstructionBaseline, 'Save baselines');
      }
    } else if (stage === CONSTRUCTION_BASELINE_SET) {
      if(milestone === _.last(this.MILESTONES) && !this.hasValuesBefore(schedule, 'actual', 'projectCloseout') ) {
        return this.renderButton(this.redoConstructionBaseline, 'Re-Baseline');
      }
    }
    return this.renderDate(milestone, name);
  }

  renderMasterEditor () {
    return this.MILESTONES.map(milestone =>
      <tr key={'m-' + milestone}>
        <td>{ this.toTitle(milestone) }</td>
        <td>{ this.renderDateField(milestone, 'original') }</td>
        <td>{ this.renderDateField(milestone, 'projected') }</td>
        <td>{ this.renderDateField(milestone, 'actual') }</td>
        <td>{ this.renderDateField(milestone, 'designBaseline') }</td>
        <td>{ this.renderDateField(milestone, 'constructionBaseline') }</td>
        <td>
          { isClientAdjustable(milestone)
            ? this.renderDateField(milestone, 'clientExtendedBaseline')
            : NBSP }
        </td>
      </tr>
    )
  }

  renderEditor () {
    const stage = this.getCurrentStage();
    return this.MILESTONES.map(milestone =>
      <tr key={'e-' + milestone}>
        <td>{ this.toTitle(milestone) }</td>
        <td>{ this.renderOriginal(milestone, stage) }</td>
        <td>{ this.renderProjected(milestone, stage) }</td>
        <td>{ this.renderActual(milestone, stage) }</td>
        <td>{ this.renderDesignBaseline(milestone, stage) }</td>
        <td>{ this.renderConstructionBaseline(milestone, stage) }</td>
        <td>{ this.renderClientBaseline(milestone, stage) }</td>
      </tr>
    )
  }

  renderButtons () {
    const { isSaving, allValid, batch } = this.state;
    const canSave = allValid && !_.isEmpty(batch) && !this.state.settingNow;

    if (isSaving) {
      return <div className="loader"></div>
    } else if (this.state.masterMode) {
      return (
        <div className="section-buttons">
          <button className="btn-cancel" disabled={!this.undos.length} onClick={this.onUndo}>Undo</button>
          <button className="btn-cancel" onClick={this.props.onCancel}>Exit</button>
          <button className="btn-save" disabled={!canSave} onClick={this.handleSave}>Save</button>
        </div>
      );
    }

    let saveMethod = this.handleSave;
    if (this.state.settingNow === 'projected') saveMethod = this.askToVerifyProjected;

    return (
      <div className="section-buttons">
        {/*<button className="btn-clear" onClick={this.clearWhole}>Clear</button>*/}
        { this.props.showMasterEdit && <button className="btn-cancel" onClick={this.toggleMasterMode}>Master Edit</button> }
        <button className="btn-cancel" disabled={!this.undos.length} onClick={this.onUndo}>Undo</button>
        <button className="btn-cancel" onClick={this.handleExit}>Exit</button>
        <button className="btn-save" disabled={!canSave} onClick={saveMethod}>Save</button>
      </div>
    )
  }
  // end renderColumn methods }}}

  render () {
    return (
      <div className="col s12">
        <div className="table-wrap">
          <table className="with-dates">
            <thead>
              <tr>
                <th>Milestone</th>
                <th>Original</th>
                <th>Projected</th>
                <th>Actual</th>
                <th>Design Baseline</th>
                <th>Construction Baseline</th>
                <th>Client Ext. Baseline</th>
              </tr>
            </thead>
            <tbody>
              { this.state.masterMode
                ? this.renderMasterEditor()
                : this.renderEditor() }
            </tbody>
          </table>
        </div>
        { this.renderModal() }
        { this.renderAlertModal() }
        { this.renderButtons() }
        <div className="schedule-help"></div>
      </div>
    )
  }
}

ScheduleForm.propTypes = {
  divisionName: PropTypes.string.isRequired,
  onCancel: PropTypes.func.isRequired,
  save: PropTypes.func.isRequired,
  schedule: PropTypes.object.isRequired,
  scheduleStage: PropTypes.string,
  showMasterEdit: PropTypes.bool
};

export default ScheduleForm;